import { AxiosInstance } from "axios";
import { CoverageTypeEnum } from "@platform/types";
import { logger } from "@platform/browser-logger";

import {
  RfpDocumentsSummaryResponse,
  UploadDocumentsResponse,
  ProcessDocumentResponse,
  PlanBuilderResponse,
  RenameDocumentResponse,
} from "./types";

import type {
  BenefitFieldMeta,
  BenefitPlanFieldsResponse,
  PlanFieldsMeta,
} from "./benefit-plan-fields-types";
import type { UploadedDocument } from "../types";

const BASE_URL = "/ai-dox/v1";
const BFF_BASE_URL = "/gateway";

/**
 * Maps frontend CoverageTypeEnum values to canonical backend coverage types.
 * The backend uses "dental" (not "dental-ppo"/"dental-hmo") because the
 * PPO/HMO distinction is determined at the benefit-plan level during AI
 * extraction, not at the coverage level.
 */
const COVERAGE_TYPE_TO_CANONICAL: Partial<
  Record<CoverageTypeEnum, CoverageTypeEnum>
> = {
  [CoverageTypeEnum.DENTAL_PPO]: CoverageTypeEnum.DENTAL,
  [CoverageTypeEnum.DENTAL_HMO]: CoverageTypeEnum.DENTAL,
};

function toCanonicalCoverageType(
  coverageType: CoverageTypeEnum,
): CoverageTypeEnum {
  return COVERAGE_TYPE_TO_CANONICAL[coverageType] ?? coverageType;
}

/**
 * Derives a product-type hint ("ppo" or "hmo") from the user's dental
 * sub-type selection. The backend stores this on the join table as a
 * fallback for when AI extraction hasn't determined the sub-type yet.
 * A document assigned to both sub-types sends no hint, so extraction keeps each
 * section's own sub-type rather than forcing every plan to one of them.
 */
function toProductTypeHint(coverages: CoverageTypeEnum[]): string | undefined {
  const isHmo = coverages.includes(CoverageTypeEnum.DENTAL_HMO);
  const isPpo = coverages.includes(CoverageTypeEnum.DENTAL_PPO);

  if (isHmo && isPpo) return undefined;
  if (isHmo) return "hmo";
  if (isPpo) return "ppo";
  return undefined;
}

export const getDocumentsSummary = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<RfpDocumentsSummaryResponse> => {
  logger.debug("[Document Library API] Fetching documents summary from BFF:", {
    rfpId,
  });

  const response = await axiosInstance.get<RfpDocumentsSummaryResponse>(
    `${BFF_BASE_URL}/plan-builder/${rfpId}/documents-summary`,
  );

  logger.debug("[Document Library API] Fetched documents summary:", {
    rfpId,
    total: response.data.total,
  });

  return response.data;
};

export const uploadDocuments = async (
  rfpId: string,
  brokerId: number,
  brokerPortalRfpId: number,
  documents: UploadedDocument[],
  axiosInstance: AxiosInstance,
): Promise<UploadDocumentsResponse> => {
  logger.debug("[Document Library API] Uploading documents:", {
    rfpId,
    brokerId,
    brokerPortalRfpId,
    documentsCount: documents.length,
  });

  const formData = new FormData();
  documents.forEach((doc) => formData.append("files", doc.file));

  const metadata = documents.map((doc) => ({
    fileName: doc.name,
    documentType: doc.documentType,
    coverageTypes: [
      ...new Set((doc.assignedCoverage ?? []).map(toCanonicalCoverageType)),
    ],
    carrierName: doc.assignedCarrier,
    productTypeHint: toProductTypeHint(doc.assignedCoverage ?? []),
  }));
  formData.append("metadata", JSON.stringify(metadata));
  formData.append(
    "payload",
    JSON.stringify({
      brokerId,
      rfpId: brokerPortalRfpId,
    }),
  );

  const response = await axiosInstance.post<UploadDocumentsResponse>(
    `${BASE_URL}/rfps/${rfpId}/documents`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );

  logger.debug("[Document Library API] Upload complete:", {
    rfpId,
    uploadedCount: response.data.documents.length,
  });

  return response.data;
};

export const processDocument = async (
  rfpId: string,
  documentId: string,
  axiosInstance: AxiosInstance,
  planEffectiveDate?: string,
): Promise<ProcessDocumentResponse> => {
  logger.debug("[Document Library API] Processing document with AI:", {
    rfpId,
    documentId,
  });

  const url = planEffectiveDate
    ? `${BASE_URL}/rfps/${rfpId}/documents/${documentId}/process?plan_effective_date=${encodeURIComponent(planEffectiveDate)}`
    : `${BASE_URL}/rfps/${rfpId}/documents/${documentId}/process`;

  const response = await axiosInstance.post<ProcessDocumentResponse>(url);

  logger.debug("[Document Library API] Process dispatched:", {
    rfpId,
    documentId,
    workflowId: response.data.workflowId,
  });
  return response.data;
};

export const renameDocument = async (
  rfpId: string,
  documentId: string,
  newDocumentName: string,
  axiosInstance: AxiosInstance,
): Promise<RenameDocumentResponse> => {
  logger.debug("[Document Library API] Renaming document:", {
    rfpId,
    documentId,
    newDocumentName,
  });

  const response = await axiosInstance.patch<RenameDocumentResponse>(
    `${BASE_URL}/rfps/${rfpId}/documents/${documentId}/name`,
    { newDocumentName },
  );

  logger.debug("[Document Library API] Document renamed:", {
    rfpId,
    documentId,
    name: response.data.name,
  });

  return response.data;
};

export const removeDocument = async (
  rfpId: string,
  documentId: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  logger.debug("[Document Library API] Removing document:", {
    rfpId,
    documentId,
  });

  await axiosInstance.delete(
    `${BFF_BASE_URL}/plan-builder/${rfpId}/documents/${documentId}`,
  );

  logger.debug("[Document Library API] Document removed:", {
    rfpId,
    documentId,
  });
};

export const removeDocumentCoverages = async (
  rfpId: string,
  documentId: string,
  coverages: { coverageType: string }[],
  axiosInstance: AxiosInstance,
): Promise<void> => {
  logger.debug("[Document Library API] Removing document coverages:", {
    rfpId,
    documentId,
    coverages,
  });

  await axiosInstance.delete(
    `${BFF_BASE_URL}/plan-builder/${rfpId}/documents/${documentId}/coverages`,
    {
      data: { coverages },
    },
  );

  logger.debug("[Document Library API] Document coverages removed:", {
    rfpId,
    documentId,
    coverages,
  });
};

export const getPlanBuilderData = async (
  rfpId: string,
  axiosInstance: AxiosInstance,
): Promise<PlanBuilderResponse> => {
  logger.debug("[Document Library API] Fetching plan builder data from BFF:", {
    rfpId,
  });

  const response = await axiosInstance.get<PlanBuilderResponse>(
    `${BFF_BASE_URL}/plan-builder/${rfpId}`,
  );

  return response.data;
};

export const getBenefitPlanFields = async (
  documentId: string,
  axiosInstance: AxiosInstance,
): Promise<BenefitPlanFieldsResponse> => {
  logger.debug(
    "[Document Library API] Fetching benefit plan fields from BFF:",
    {
      documentId,
    },
  );

  const response = await axiosInstance.get<BenefitPlanFieldsWireResponse>(
    `${BFF_BASE_URL}/documents/${documentId}/benefit-plan-fields`,
  );

  return withFieldSources(response.data);
};

/** A BFF deployed before field sources existed sends fields without one. */
type BenefitPlanFieldsWireResponse = Omit<
  BenefitPlanFieldsResponse,
  "plans"
> & {
  plans: Record<
    string,
    Omit<PlanFieldsMeta, "fields"> & {
      fields: (Omit<BenefitFieldMeta, "source"> &
        Partial<Pick<BenefitFieldMeta, "source">>)[];
    }
  >;
};

const withFieldSources = (
  response: BenefitPlanFieldsWireResponse,
): BenefitPlanFieldsResponse => ({
  ...response,
  plans: Object.fromEntries(
    Object.entries(response.plans).map(([btxIdentifier, plan]) => [
      btxIdentifier,
      {
        ...plan,
        fields: plan.fields.map((field) => ({
          ...field,
          source: field.source ?? "extraction",
        })),
      },
    ]),
  ),
});

export const downloadRfpFile = async (
  downloadLink: string,
  axiosInstance: AxiosInstance,
): Promise<Blob> => {
  logger.debug("[API] Downloading RFP file", { downloadLink });

  const response = await axiosInstance.post<Blob>(
    "brokerportal/v1/getRfpFile",
    {
      downloadLink,
    },
    { responseType: "blob" },
  );
  return response.data;
};
